// ImportPreviewDialog.js
// Extracted from AIWorkforcePage.js for better maintainability

import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Label } from '../../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { UserPlus, RefreshCw, Target } from 'lucide-react';

/**
 * Dialog for previewing and importing data to prospects
 * 
 * @param {Object} props
 * @param {boolean} props.open - Controls dialog visibility
 * @param {function} props.onOpenChange - Callback when dialog open state changes
 * @param {Object} props.importPreview - Preview data with headers, rows, etc.
 * @param {Object} props.fieldMappings - Current field mappings
 * @param {function} props.onFieldMappingChange - Callback when field mapping changes
 * @param {string} props.selectedCampaignId - Currently selected campaign ID
 * @param {function} props.onCampaignChange - Callback when campaign selection changes
 * @param {Array} props.campaigns - List of available campaigns
 * @param {function} props.onLoadCampaigns - Callback to load campaigns
 * @param {boolean} props.importLoading - Whether import is in progress
 * @param {function} props.onImport - Callback when import is clicked
 * @param {function} props.onCancel - Callback when cancel is clicked
 */
export const ImportPreviewDialog = ({
  open,
  onOpenChange,
  importPreview,
  fieldMappings,
  onFieldMappingChange,
  selectedCampaignId,
  onCampaignChange,
  campaigns = [],
  onLoadCampaigns,
  importLoading = false,
  onImport,
  onCancel
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5 text-emerald-500" />
            Import to Prospects
          </DialogTitle>
          <DialogDescription>
            Review and map the data before importing to the Prospects tab
          </DialogDescription>
        </DialogHeader>
        
        {importPreview && (
          <div className="space-y-4 py-4">
            <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
              <p className="text-sm text-amber-700">
                <strong>{importPreview.total_rows}</strong> rows detected. Map each column to the appropriate prospect field below.
              </p>
            </div>
            
            {/* Field Mapping */}
            <div>
              <Label className="text-sm font-medium mb-2 block">Column Mapping</Label>
              <div className="grid grid-cols-2 gap-3">
                {importPreview.detected_headers?.map((header, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <span className="text-sm font-medium w-32 truncate" title={header}>{header}</span>
                    <span className="text-muted-foreground">→</span>
                    <Select
                      value={fieldMappings[header] || 'skip'}
                      onValueChange={(val) => onFieldMappingChange({...fieldMappings, [header]: val})}
                    >
                      <SelectTrigger className="w-40" data-testid={`mapping-${header}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {importPreview.mapping_options?.map(opt => (
                          <SelectItem key={opt} value={opt}>
                            {opt.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Sample Data Preview */}
            <div>
              <Label className="text-sm font-medium mb-2 block">Sample Data Preview</Label>
              <div className="border rounded-lg overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50">
                    <tr>
                      {importPreview.detected_headers?.map((header, idx) => (
                        <th key={idx} className="px-3 py-2 text-left font-medium border-b">
                          {header}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {importPreview.sample_rows?.slice(0, 3).map((row, rowIdx) => (
                      <tr key={rowIdx} className="border-b">
                        {row.map((cell, cellIdx) => (
                          <td key={cellIdx} className="px-3 py-2 truncate max-w-[150px]" title={cell}>
                            {cell || '-'}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            
            {/* Campaign Selection */}
            <div>
              <Label className="text-sm font-medium mb-2 block flex items-center gap-2">
                <Target className="h-4 w-4" />
                Link to Sales Campaign (Optional)
              </Label>
              <Select
                value={selectedCampaignId || 'none'}
                onValueChange={(v) => onCampaignChange(v === 'none' ? '' : v)}
              >
                <SelectTrigger data-testid="import-campaign-select">
                  <SelectValue placeholder="Select a campaign..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No campaign - import as standalone prospects</SelectItem>
                  {campaigns.map(campaign => (
                    <SelectItem key={campaign.id} value={campaign.id}>
                      {campaign.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">
                Link imported prospects to a campaign for tracking and follow-up
              </p>
              {campaigns.length === 0 && onLoadCampaigns && (
                <Button 
                  variant="link" 
                  size="sm" 
                  className="p-0 h-auto text-xs mt-1"
                  onClick={onLoadCampaigns}
                  data-testid="load-campaigns-btn"
                >
                  Load campaigns
                </Button>
              )}
            </div>
          </div>
        )}
        
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} data-testid="import-preview-cancel">
            Cancel
          </Button>
          <Button
            disabled={importLoading}
            onClick={onImport}
            data-testid="import-preview-submit"
          >
            {importLoading ? (
              <>
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                Importing...
              </>
            ) : (
              <>
                <UserPlus className="h-4 w-4 mr-2" />
                Import {importPreview?.total_rows || 0} Prospects
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ImportPreviewDialog;
